import type { Session } from '@halo/chat-sdk-react';
import { StateEnum, type Msg } from '@halo/chat-sdk-react';
import { MessageHeader, useEditorContext } from '@halo/ui-chat-react';
import { useIsMyMsg, useUserInfo } from '@/scripts/hooks';
import { useI18nStore } from '@/store/i18n';
import { useSessionContentsContext } from '../session-parts';

export interface MsgHeaderProps {
  message: Msg;
  session: Session;
  userNameAfter?: ReactNode;
}

export const MsgHeader = memo(function MsgHeader({
  message,
  session,
  userNameAfter,
  ...props
}: MsgHeaderProps) {
  const { t } = useTranslation();
  const isMyMessage = useIsMyMsg(message);
  const { user } = useUserInfo(message.owner_id);
  const { editor } = useEditorContext();
  const { canMention } = useSessionContentsContext();
  const insertMention = useMemo(() => {
    return canMention
      ? () => {
          if (user) {
            editor.insertMention(user);
            editor.focus();
          }
        }
      : undefined;
  }, [user, editor, canMention]);
  const formatTime = useI18nStore((store) => store.formatTime);
  const createdAtDisplay = useMemo(
    () => (message.created_at ? formatTime(message.created_at) : undefined),
    [message.created_at, formatTime]
  );

  return (
    <MessageHeader
      createdAtDisplay={createdAtDisplay?.fullTitle}
      userName={
        <>
          {user?.state === StateEnum.DELETE_ACCOUNT
            ? t('chat.deleteAccountText')
            : user?.name || ''}{' '}
          {userNameAfter}
        </>
      }
      isMe={isMyMessage}
      onMentionClick={insertMention}
      {...props}
    />
  );
});
